import * as XLSX from 'xlsx';
import { reportTableModel, columnLabel } from './reportTableModel';
import { analyticsService } from '../services/analyticsService';

function sanitizeSheetName(name) {
  if (!name) return 'Report';
  const sanitized = name.replace(/[\\/?*[\]:]/g, ' ').trim();
  return sanitized.slice(0, 31) || 'Report';
}

function sanitizeFileName(name) {
  if (!name) return 'analytics_report';
  return name
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_');
}

function autoFitColumns(rows) {
  if (!rows || !rows.length) return [];
  const colCount = Math.max(...rows.map((r) => (Array.isArray(r) ? r.length : 0)));
  const widths = [];
  for (let c = 0; c < colCount; c += 1) {
    let maxLen = 10;
    rows.forEach((row) => {
      const val = row[c] != null ? String(row[c]) : '';
      if (val.length > maxLen) {
        maxLen = Math.min(val.length, 60);
      }
    });
    widths.push({ wch: maxLen + 3 });
  }
  return widths;
}

function normalizeCellValue(val) {
  if (val == null) return '';
  if (typeof val === 'number') return val;
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') {
    if (val.display != null) return val.display;
    if (val.label != null) return val.label;
    if (val.name != null) return val.name;
    if (Array.isArray(val)) return val.length;
    return JSON.stringify(val);
  }
  return String(val);
}

/**
 * Exports report data to a genuine formatted Excel (.xlsx) file.
 * Handles metric summaries, time-series/compare charts, and multi-column tables.
 */
export async function exportReportToExcel({
  reportName = 'Report',
  result = null,
  querySpec = null,
  orgId = null,
  columnLabels = {},
  categoryLabel = '',
  measureLabel = '',
}) {
  let activeResult = result;

  // If result is not in memory, run query to retrieve it
  if (!activeResult && querySpec && orgId) {
    try {
      const queryRes = await analyticsService.runQuery(querySpec, orgId);
      if (queryRes.ok && queryRes.data?.result) {
        activeResult = queryRes.data.result;
      }
    } catch {
      // Continue to fallback
    }
  }

  let sheetData = [];

  // 1. Metric summary (e.g., "All template report" KPI overview)
  if (Array.isArray(activeResult?.metrics) && activeResult.metrics.length) {
    sheetData = [
      ['Metric', 'Count / Value'],
      ...activeResult.metrics.map((item) => {
        const val = typeof item.value === 'number'
          ? item.value
          : (item.display != null ? item.display : item.value ?? 0);
        return [item.label || item.id, val];
      }),
    ];
  }
  // 2. Chart categories and series (trend or compare charts)
  else if (
    Array.isArray(activeResult?.categories) &&
    activeResult.categories.length &&
    Array.isArray(activeResult?.series) &&
    activeResult.series.length
  ) {
    const headerRow = [
      categoryLabel || 'Date / Category',
      ...activeResult.series.map((s) => s.label || s.name || s.id || 'Value'),
    ];
    const dataRows = activeResult.categories.map((cat, rowIdx) => [
      cat ?? '',
      ...activeResult.series.map((s) => {
        const point = s.data?.[rowIdx];
        return typeof point === 'number' ? point : (point ?? 0);
      }),
    ]);
    sheetData = [headerRow, ...dataRows];
  }
  // 3. Single KPI / number
  else if (activeResult?.kind === 'number' && (activeResult.value != null || activeResult.display != null)) {
    const val = typeof activeResult.value === 'number'
      ? activeResult.value
      : (activeResult.display != null ? activeResult.display : activeResult.value);
    sheetData = [
      ['Metric', 'Value'],
      [activeResult.label || reportName || 'Value', val],
    ];
  }
  // 4. Tabular data / rows & columns (e.g., Intervene agent performance or records table)
  else {
    const model = reportTableModel(activeResult);
    if (model.columns?.length && model.rows?.length) {
      const headerRow = model.columns.map((col) =>
        columnLabel(col, { categoryLabel, measureLabel, columnLabels })
      );
      const dataRows = model.rows.map((row) =>
        model.columns.map((col) => normalizeCellValue(row[col]))
      );
      sheetData = [headerRow, ...dataRows];
    }
  }

  // Fallback to backend export if no in-memory data could be parsed
  if (!sheetData.length) {
    if (querySpec && orgId) {
      await analyticsService.downloadExport(querySpec, orgId);
      return;
    }
    throw new Error('No data available to export');
  }

  // Generate Excel workbook
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  ws['!cols'] = autoFitColumns(sheetData);
  const sheetName = sanitizeSheetName(reportName);
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const cleanFileName = `${sanitizeFileName(reportName)}.xlsx`;
  XLSX.writeFile(wb, cleanFileName);
}

export default exportReportToExcel;

